import { customElement, property, state } from 'lit/decorators.js';
import { html, LitElement, TemplateResult } from 'lit';

/**
 * One entry in a <wm-context-menu>. `run` is the whole binding: the menu closes itself first and
 * then calls it with the originating click, so an action that needs the user's gesture (writing to
 * the clipboard, opening a file dialog) still has it.
 */
export interface ContextMenuAction {
  /** Stable identifier. Not shown; it exists so callers can key and test their own action lists. */
  id: string;
  label: string;
  /** Font-awesome icon name, exactly as webmodule-icon takes it. */
  icon?: string;
  disabled?: boolean;
  /** Draws a divider above this item. */
  separatorBefore?: boolean;
  run: (event: Event) => unknown;
}

/**
 * <wm-context-menu>
 *
 * A right-click menu for the element it sits in. Put one anywhere inside the region it belongs to
 * and bind the actions:
 *
 *   <wm-context-menu .actions=${this.menuActions()} zone=".single-file-drop-container"></wm-context-menu>
 *
 * It occupies no space (display: contents) and does not wrap anything, so it can be dropped into an
 * existing template without disturbing the layout or breaking child selectors. It listens on its
 * parent element; `zone` narrows that to the parts of the parent worth right-clicking, and anywhere
 * else - or an empty action list - is left to the browser's own menu, which is what a user wants
 * when there is nothing of ours to offer.
 */
@customElement('wm-context-menu')
export class WebModuleContextMenu extends LitElement {
  @property({ attribute: false }) actions: ContextMenuAction[] = [];
  /** Optional CSS selector: the right-click must land on or inside a match for the menu to open. */
  @property() zone = '';

  @state() private origin: { x: number; y: number } | null = null;

  // The parent as it was when we attached. parentElement is already null by disconnectedCallback,
  // so the listener could not otherwise be removed from the element it was added to.
  private host: HTMLElement | null = null;

  // The right-click that opened the menu. Listeners added mid-dispatch still run for the event that
  // is dispatching - the document sees it on the way up, after we have handled it on the host - so
  // without this the menu would dismiss itself the instant it opened.
  private openedBy: Event | null = null;

  // Light DOM: the menu itself is the component library's and brings its own styling, and a menu
  // that positions against the viewport belongs in the page rather than behind a shadow boundary.
  createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.style.display = 'contents';
    this.host = this.parentElement;
    this.host?.addEventListener('contextmenu', this.eventContextMenu);
  }

  disconnectedCallback(): void {
    this.host?.removeEventListener('contextmenu', this.eventContextMenu);
    this.host = null;
    this.close();
    super.disconnectedCallback();
  }

  /** Closes the menu if it is open. Safe to call when it is not. */
  public close(): void {
    if (!this.origin) return;
    this.origin = null;
    this.openedBy = null;
    document.removeEventListener('click', this.eventDismiss);
    document.removeEventListener('contextmenu', this.eventDismiss);
    document.removeEventListener('keydown', this.eventKeyDown, true);
    window.removeEventListener('resize', this.eventDismiss, true);
    window.removeEventListener('scroll', this.eventDismiss, true);
  }

  private eventContextMenu = (e: MouseEvent) => {
    if (this.actions.length === 0) return;
    if (this.zone && !(e.target as Element | null)?.closest?.(this.zone)) return;

    e.preventDefault();
    this.origin = { x: e.clientX, y: e.clientY };
    this.openedBy = e;
    document.addEventListener('click', this.eventDismiss);
    document.addEventListener('contextmenu', this.eventDismiss);
    document.addEventListener('keydown', this.eventKeyDown, true);
    window.addEventListener('resize', this.eventDismiss, true);
    window.addEventListener('scroll', this.eventDismiss, true);
  };

  private eventDismiss = (e: Event) => {
    if (e === this.openedBy) return;
    this.close();
  };

  private eventKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') this.close();
  };

  private select(action: ContextMenuAction, e: Event) {
    if (action.disabled) return;
    // Close first: the action may open a dialog, and a menu left hanging over it looks broken.
    this.close();
    void action.run(e);
  }

  /**
   * Nudges the menu back inside the viewport once it has been rendered and its size is known. Done
   * here rather than from an estimate, because the menu is as wide as its longest label.
   */
  protected updated(): void {
    const menu = this.renderRoot.querySelector<HTMLElement>('.wm-context-menu');
    if (!menu || !this.origin) return;

    const { width, height } = menu.getBoundingClientRect();
    menu.style.left = `${Math.max(4, Math.min(this.origin.x, window.innerWidth - width - 8))}px`;
    menu.style.top = `${Math.max(4, Math.min(this.origin.y, window.innerHeight - height - 8))}px`;
  }

  render(): TemplateResult {
    if (!this.origin) return html``;

    // Clicks inside the menu must not reach the document dismisser: each item closes the menu
    // itself, after deciding it was actually chosen.
    const swallow = (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
    };

    return html`<div
      class="wm-context-menu"
      style="position: fixed; left: ${this.origin.x}px; top: ${this.origin.y}px; z-index: 1080;"
      @click=${swallow}
      @contextmenu=${swallow}
    >
      <webmodule-menu>
        ${this.actions.map(
          action => html`
            ${action.separatorBefore ? html`<webmodule-divider></webmodule-divider>` : html``}
            <webmodule-menu-item ?disabled=${action.disabled} @click=${(e: Event) => this.select(action, e)}>
              ${action.icon
                ? html`<webmodule-icon slot="prefix" library="fa" name=${action.icon}></webmodule-icon>`
                : html``}
              ${action.label}
            </webmodule-menu-item>
          `
        )}
      </webmodule-menu>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wm-context-menu': WebModuleContextMenu;
  }
}
